import { useMemo } from 'react'
import { ChevronDown, Folder, Plus, Check, Music } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Node } from 'reactflow'
import type { ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { buildAssetCandidates, type CanvasAssetCandidate } from '../../nodes/shared/mentionUtils'
import { getAcceptedReferenceMediaTypes } from '../../nodes/shared/nodeReferenceCapabilities'
import { getAssetUrl } from '../../../../api/useAssetUrl'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

export function mediaIdentity(ref: Pick<CanvasAssetCandidate, 'nodeId' | 'sourceHandle' | 'fromIndex' | 'mediaType'>): string {
  return `${ref.nodeId}:${ref.sourceHandle ?? ref.mediaType}:${ref.fromIndex ?? ''}`
}
function MediaCard({ asset, imported, disabled, onAdd }: { asset: CanvasAssetCandidate; imported: boolean; disabled: boolean; onAdd: () => void }) {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const url = useAuthenticatedMediaUrl(projectId ? getAssetUrl(projectId, asset.assetPath) : null)
  const supported = getAcceptedReferenceMediaTypes('video_editor').includes(asset.mediaType)
  return <div className="overflow-hidden rounded-lg border border-border bg-surface-1" data-testid="all-media-card">
    <div className="flex h-20 items-center justify-center bg-input-bg">
      {asset.mediaType === 'audio' ? <Music className="h-6 w-6 text-muted-foreground" /> : asset.mediaType === 'image'
        ? <img src={url ?? undefined} alt={asset.label} className="h-full w-full object-contain" loading="lazy" />
        : <video src={url ?? undefined} muted preload="metadata" className="h-full w-full object-contain" />}
    </div>
    <p className="truncate px-2 pt-2 text-sm" title={asset.label}>{asset.label}</p>
    <div className="flex items-center justify-between gap-1 p-2"><span className="text-xs text-muted-foreground">{t(`editorWorkspace.${asset.mediaType}`)}</span>
      <button type="button" onClick={onAdd} disabled={disabled || imported || !supported}
        title={!supported ? t('editorWorkspace.unsupportedImage') : undefined}
        className="flex h-7 items-center gap-1 rounded-lg border border-input-border bg-surface-1 px-2 text-xs hover:bg-surface-3 hover:border-input-border-hover focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50">
        {imported ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}{t(imported ? 'editorWorkspace.importedLabel' : 'editorWorkspace.add')}
      </button>
    </div>
    {!supported && <p className="px-2 pb-2 text-xs text-text-muted">{t('editorWorkspace.unsupportedImage')}</p>}
  </div>
}
export function AllMediaPanel({ nodes, nodeId, imported, disabled, onAdd }: {
  nodes: Node<ReactFlowCanvasNodeData>[]; nodeId: string; imported: CanvasAssetCandidate[]; disabled: boolean; onAdd: (asset: CanvasAssetCandidate) => void
}) {
  const { t } = useTranslation('canvas')
  const folders = useMemo(() => {
    const result = new Map<string, { id: string; label: string; assets: CanvasAssetCandidate[] }>()
    const byId = new Map(nodes.map((node) => [node.id, node]))
    for (const asset of buildAssetCandidates(nodes, nodeId)) {
      const parentId = byId.get(asset.nodeId)?.parentId
      const parent = parentId ? byId.get(parentId) : undefined
      const id = parent?.id ?? 'ungrouped'
      const label = parent && typeof parent.data.label === 'string' ? parent.data.label : parent?.id ?? t('editorWorkspace.ungrouped')
      const folder = result.get(id) ?? { id, label, assets: [] }
      folder.assets.push(asset); result.set(id, folder)
    }
    return [...result.values()]
  }, [nodes, nodeId, t])
  const importedKeys = new Set(imported.map(mediaIdentity))
  return <div className="h-full overflow-y-auto p-3" data-testid="all-media-panel">
    {!folders.length && <p className="text-sm text-muted-foreground">{t('editorWorkspace.noMedia')}</p>}
    {folders.map((folder) => <details key={folder.id} open className="mb-3 rounded-lg border border-border bg-surface-0" data-testid="media-folder">
      <summary className="flex cursor-pointer items-center gap-2 p-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-primary"><ChevronDown className="h-3 w-3" /><Folder className="h-4 w-4" /><span className="truncate">{folder.label}</span><span className="ml-auto text-xs text-muted-foreground">{folder.assets.length}</span></summary>
      <div className="grid grid-cols-2 gap-2 p-2 pt-0">{folder.assets.map((asset) => <MediaCard key={mediaIdentity(asset)} asset={asset}
        imported={importedKeys.has(mediaIdentity(asset))} disabled={disabled} onAdd={() => onAdd(asset)} />)}</div>
    </details>)}
  </div>
}
